import { EditIcon } from "@fluentui/react-northstar";
import React, { useEffect, useRef, useState } from "react";
import { ReactEditor, useFocused, useSelected, useSlateStatic } from "slate-react";
import { Transforms } from "slate";
import ScopeTextLighhSVG from '../../../../svg/ScopeIcons/text/light.svg';
import ScopeTextDarkSVG from '../../../../svg/ScopeIcons/text/dark.svg';
import { imageStyle } from "./slate-editor-helpers";
import { themeNames } from "@fluentui/react-teams";
import { CustomButtonData } from "./HelpCardAddButton";
import { ScopePermissionPeopleComponent } from "../../../Rbac/ScopePermissionPeopleComponent";

export const El = (props) => {
  const { attributes, children, element } = props;
  const style = { textAlign: element.align };
  switch (element.type) {
    case "h1":
      return (
        <h1 className={"text-6xl"} style={style} {...attributes}>
          {children}
        </h1>
      );
    case "h2":
      return (
        <h2 className={"text-5xl"} style={style} {...attributes}>
          {children}
        </h2>
      );
    case "h3":
      return (
        <h3 className={"text-4xl"} style={style} {...attributes}>
          {children}
        </h3>
      );
    case "code":
      return (
        <pre style={style} {...attributes}>
          {children}
        </pre>
      );
    case "heading-four":
      return (
        <h2 className={"text-3xl"} style={style} {...attributes}>
          {children}
        </h2>
      );
    case "bulleted-list":
      return (
        <ul className="mx-4 list-disc" style={style} {...attributes}>
          {children}
        </ul>
      );
    case "numbered-list":
      return (
        <ol className="mx-4 list-decimal" style={style} {...attributes}>
          {children}
        </ol>
      );
    case "image":
      return <Image {...props} />;
    case "mention":
      return <Mention {...props} />;
    case "list-item":
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case "link":
      if (element.url.startsWith('%7B%22Button%22:')){
        console.log("link element",props.theme);
        const customAttr = {"data-value":element.url}
        let url = JSON.parse(decodeURIComponent(element.url)) as CustomButtonData;
        // ScopingDetails is stripped out before the button data is encoded into the
        // href (kept lean since it round-trips through the DOM), so the member list
        // has to come from the live scopedButtons state instead - which also means
        // this reflects the current in-editor selection even before the help card
        // itself is saved.
        const buttonScopedMembers = props.scopedButtons?.find(x => x.ButtonId == url.ButtonId)?.ScopingDetails?.ScopedMembers ?? [];
        return (
          <a
            onClick={(e)=>e.preventDefault()}
            href={element.url}
            className="wysiwyg-mention"
            style={{
              color: "var(--mgt-theme-brand-foreground1)",
              cursor: "pointer",
            }}
            {...customAttr}
          >
            {children}
            {url.IsScoped &&
              <ScopePermissionPeopleComponent
                scopedMembers={buttonScopedMembers}
                trigger={<span contentEditable={false} className={`scopedCustomButton ${props.theme === "dark" ? "scopedCustomButtonDark" : "scopedCustomButtonLight"}`}></span>}
              />
            }
            <span contentEditable={false} className="deleteCustomButton"></span>
          </a>
        );
      }
      else
      {
        return (
          <a
            href={element.url}
            style={{
              color: "var(--mgt-theme-brand-foreground1)",
              cursor: "pointer",
            }}
          >
            {children}
          </a>
        );
      }
    case "line-break":
      return <br {...attributes} />;
    case "scoped-text":
      return <>{children}</>;
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};

export const Image = ({ attributes, children, element }) => {
  const [actualWidth, setActualWidth] = useState("auto");
  const [showControls, setShowControls] = useState(false);
  const imgRef = useRef(null);
  const editor = useSlateStatic();

  const style = imageStyle(element.style);
  // Handler to change alignment
  const setAlign = (newAlign) => {
    style.float = newAlign; // Update float based on alignment
    const path = ReactEditor.findPath(editor, element);
    Transforms.setNodes(
      editor,
      { style } as any,
      { at: path }
    );
  };

    useEffect(() => {
    if (imgRef.current) {
      setActualWidth(`${imgRef.current.width}px`); // or .offsetWidth for CSS width
    }
  }, [element.url]);


    return (
    <div {...attributes}>
      <div contentEditable={false} style={{...style, width: actualWidth}}>
        <img ref={imgRef} src={element.url} style={style}
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => setShowControls(false)}
        />
          <div
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
            style={{
              position:"relative",
              bottom:"11px",
              borderRadius: 4,
              padding: "2px 4px",
              display: "flex",
              visibility:showControls ? "visible" : "hidden",
              gap: 2,
              zIndex: 2
            }}
          >
            <button
              style={{ fontSize: 12, padding: "2px 4px", background: element.style.float === "left" ? "#ddd" : "transparent", border: "none", cursor: "pointer" }}
              onMouseDown={e => { e.preventDefault(); setAlign("left"); }}
              title="Align Left"
            >L</button>
            <button
              style={{ fontSize: 12, padding: "2px 4px", background: element.style.float === "center" ? "#ddd" : "transparent", border: "none", cursor: "pointer" }}
              onMouseDown={e => { e.preventDefault(); setAlign("none"); }}
              title="Align Center"
            >C</button>
            <button
              style={{ fontSize: 12, padding: "2px 4px", background: element.style.float === "right" ? "#ddd" : "transparent", border: "none", cursor: "pointer" }}
              onMouseDown={e => { e.preventDefault(); setAlign("right"); }}
              title="Align Right"
            >R</button>
          </div>
        
      </div>
      <div style={{clear:"both"}}></div>
      {children}
    </div>
  );
};

export const Mention = ({ attributes, children, element, theme }) => {
  const selected = useSelected();
  const focused = useFocused();

  const style: React.CSSProperties = {
    padding: "3px 3px 2px",
    margin: "0 1px",
    verticalAlign: "baseline",
    display: "inline-block",
    borderRadius: "4px",
    fontSize: "0.9em",
    backgroundColor: "#eee",
    boxShadow: selected && focused ? "0 0 0 2px #B4D5FF" : "none",
  };

  if (theme === "light") {
    style.backgroundColor = "#eee";
    style.boxShadow = selected && focused ? "0 0 0 2px #B4D5FF" : "none";
  } else {
    style.backgroundColor = "#222";
    style.color = "rgb(166, 167, 220)";
  }
  // See if our empty text child has any styling marks applied and apply those
  if (element.children[0].bold) {
    style.fontWeight = "bold";
  }
  if (element.children[0].italic) {
    style.fontStyle = "italic";
  }
  return (
    <span
      {...attributes}
      contentEditable={false}
      data-cy={`mention-${element.email}`}
      style={style}
    >
      {element.character}
      {children}
    </span>
  );
};

export const Leaf = ({ attributes, children, leaf, theme, scopedButtons }) => {
  const darkCode = "border-gray-500 bg-gray-800";
  const lightCode = "border-gray-300 bg-gray-100";

  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.code) {
    children = (
      <code
        className={`border rounded-sm px-1 mx-1 ${
          theme === "dark" ? darkCode : lightCode
        }`}
      >
        {children}
      </code>
    );
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  useEffect(()=>{
    if (leaf.scoped) { 
      const scopedTexts = document.querySelectorAll(`[selectedid="${leaf.scoped.id}"].scope-text-container`);
      const count = scopedTexts.length-1;
      scopedTexts.forEach((el,i)=>{
        //This will remove the scope and edit icons on the other scope text with the same ID. edit icons should show on the last line only.
        const btn = el.querySelectorAll(".editCustomButton");
        if (i==count) {
          btn.forEach(c=>{
            c.classList.remove("hide-scope-edit-btn");
          });
          return;
        }

        btn.forEach(c=>{
          c.classList.add("hide-scope-edit-btn");
        });
      });
    }
  },[leaf.scoped]);

  if (leaf.scoped) {

    const scoped = leaf.scoped;
    const customProps = {
      "selectedId": scoped.id
    };
    const scopedMembers = scopedButtons?.find(x => x.ButtonId == scoped.id)?.ScopingDetails?.ScopedMembers ?? [];

    const onHover = (isHover) => {
      const scopedTexts = document.querySelectorAll(`[selectedid="${scoped.id}"].scope-text-container`);
      if (isHover) {
        scopedTexts.forEach(el => {
          el.classList.add("scope-text");
          const editBtn = el.querySelector(".edit-scope-text");
          if (editBtn)
            editBtn.classList.add("edit-scope-text-show");
        });
      }
      else {
        scopedTexts.forEach(el => {
          el.classList.remove("scope-text");
           const editBtn = el.querySelector(".edit-scope-text");
          if (editBtn)
            editBtn.classList.remove("edit-scope-text-show");
        });
      }

    }
    return (
      <span
        {...attributes}
        selectedid={scoped.id}
        indexid={scoped.indexid}
        count={scoped.count}
        className={`scope-text-container ${theme === "dark" ? "scope-text-dark" : "scope-text-light"}`}
        style={{ position: "relative", display: "inline", whiteSpace: "pre-wrap" }}
        onMouseOver={()=>onHover(true)}
        onMouseLeave={()=>onHover(false)}
      >
        <span {...attributes}
        style={{
          display: "inline-block"
        }}
        >
          {children}
        </span>
        <ScopePermissionPeopleComponent
          scopedMembers={scopedMembers}
          trigger={<span {...customProps} className="editCustomButton scope-text-icon">{theme === "dark" ? <ScopeTextDarkSVG /> : <ScopeTextLighhSVG />}</span>}
        />
        <span {...customProps} className="editCustomButton edit-scope-text"><EditIcon  outline /></span>
        
      </span>
    );
  }

  return <span {...attributes}>{children}</span>;
};
